import {atom} from 'jotai';
import {generateUUID} from '@/lib/util.ts';

/**
 * A transient "nudge" — something just happened that's worth a one-click
 * action right now (a scan finished, a mismatch was just detected), as
 * opposed to the durable state for the same thing (e.g. RunTicketsPage's
 * own Needs Review / Unmatched chips, which stay accurate whether or not
 * this was ever seen or dismissed). See docs/run-ticket-scan-spec.md and
 * the design discussion in this session's history for why both exist:
 * a toast alone can be missed (app backgrounded, several fire while the
 * user's mid-task, ...); durable state alone is easy to forget to check.
 * Together, missing the nudge just means you find out the next time you
 * look at the durable indicator instead of never finding out at all.
 */
export interface AppNotification {
   id: string;
   message: string;
   /** Label for the one action button, e.g. "Review". Omit for a plain informational nudge with no action. */
   actionLabel?: string;
   onAction?: () => void;
   severity?: 'info' | 'warning' | 'success';
   /**
    * Technical text (e.g. the server's error message) shown only after the
    * user taps "Show details". The notification stays up until dismissed
    * (no auto-hide) so there's time to read it. ADDED 2026-10-08.
    */
   details?: string;
   /**
    * FIXED 2026-08-29 (Duane, second report — same complaint as the same
    * day's total-vs-delta fix in useRunTicketReconciliation.ts, but a
    * DIFFERENT root cause): "the notification is still wrong... filter
    * shows 5, notification says 20." The first fix made the toast
    * display the true CURRENT total at the moment it was pushed instead
    * of an incremental delta — correct, but `message` is a plain string
    * baked in at push time and never touches live data again. Toasts
    * auto-hide after 8s (see NotificationSnackbar's AUTO_HIDE_MS) but
    * stay QUEUED until dismissed/actioned if the user doesn't see them
    * right away, and the underlying backlog keeps moving every 30s poll
    * — so a toast that was 100% correct the instant it was pushed can
    * still read as stale/wrong by the time someone actually looks at it,
    * if the real total changed in between (which is common on a shared
    * multi-tester backend). Setting `kind` here tells NotificationSnackbar
    * to IGNORE `message`/recompute the count-based text fresh from the
    * live atoms every render, so what's on screen always matches
    * whatever RunTicketsPage's own chips would show at that exact
    * instant — not a snapshot from whenever the toast was queued.
    * `message` is still required/used as-is for every notification that
    * doesn't set `kind` (ScanTicketButton, DailyReportPage/Panel, etc. —
    * one-off event confirmations with no live count to go stale).
    *
    * 'unexplainedTankDrops' ADDED 2026-09-30 (chunk 7 of the tank-gauge/
    * pulled-bottoms feature set) — pushed by useTankDropReconciliation.ts,
    * recomputed live from unexplainedTankDropCountAtom the same way the
    * three RunTicket-reconciliation kinds above recompute from their own
    * atoms. See that hook's own doc comment for the (deliberately
    * non-real-time) trigger this one uses, unlike the other three.
    */
   kind?: 'needsReview' | 'unmatchedTickets' | 'unmatchedReports' | 'unexplainedTankDrops';
}

/** FIFO queue — NotificationSnackbar shows one at a time and advances as each is dismissed/actioned. */
export const notificationQueueAtom = atom<AppNotification[]>([]);

export const pushNotificationAtom = atom(null, (get, set, notification: Omit<AppNotification, 'id'>) => {
   set(notificationQueueAtom, [...get(notificationQueueAtom), {...notification, id: generateUUID()}]);
});

export const dismissNotificationAtom = atom(null, (get, set, id: string) => {
   set(
      notificationQueueAtom,
      get(notificationQueueAtom).filter((n) => n.id !== id),
   );
});
